/**
 * Own captcha: proof-of-work (docs/superpowers/specs/2026-09-30-captcha-email-design.md).
 * The server signs a challenge `<payload>.<hmac>`; the client finds `k` distinct nonces such that
 * SHA-256("<id>:<nonce>") starts with `b` zero bits. Pure TS, no platform crypto and no zod: this module is
 * imported by the browser Web Worker (Web Crypto is missing on insecure origins and ~30× slower per hash).
 */

export const CAPTCHA_PURPOSES = ['register', 'login', 'password'] as const;
export type CaptchaPurpose = (typeof CAPTCHA_PURPOSES)[number];

/** Signed challenge payload (readable by the client, only the server can make one). */
export interface CaptchaPayload {
  v: 1;
  /** random salt, base64url (≤ 32 ASCII chars: the hash input stays one SHA-256 block) */
  id: string;
  p: CaptchaPurpose;
  /** leading zero bits per solution, 0..32 */
  b: number;
  /** number of distinct solutions */
  k: number;
  /** expiry, epoch ms */
  e: number;
}

export const MAX_NONCE = 0xffffffff;
const ID_RE = /^[A-Za-z0-9_-]{1,32}$/;

const K = new Int32Array([
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5, 0xd807aa98,
  0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174, 0xe49b69c1, 0xefbe4786,
  0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da, 0x983e5152, 0xa831c66d, 0xb00327c8,
  0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967, 0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13,
  0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85, 0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819,
  0xd6990624, 0xf40e3585, 0x106aa070, 0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a,
  0x5b9cca4f, 0x682e6ff3, 0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7,
  0xc67178f2,
]);
const W = new Int32Array(64);
const BUF = new Uint8Array(64);

/** First 32 bits of SHA-256 of BUF[0..len) (len ≤ 55: a single block), allocation-free. */
function firstWord(len: number): number {
  W.fill(0, 0, 16);
  for (let i = 0; i < len; i++) W[i >> 2] = W[i >> 2]! | (BUF[i]! << (24 - 8 * (i & 3)));
  W[len >> 2] = W[len >> 2]! | (0x80 << (24 - 8 * (len & 3)));
  W[15] = len * 8;
  for (let t = 16; t < 64; t++) {
    const a = W[t - 15]!;
    const b = W[t - 2]!;
    W[t] =
      (((a >>> 7) | (a << 25)) ^ ((a >>> 18) | (a << 14)) ^ (a >>> 3)) +
      (((b >>> 17) | (b << 15)) ^ ((b >>> 19) | (b << 13)) ^ (b >>> 10)) +
      W[t - 16]! +
      W[t - 7]!;
  }
  let a = 0x6a09e667 | 0;
  let b = 0xbb67ae85 | 0;
  let c = 0x3c6ef372 | 0;
  let d = 0xa54ff53a | 0;
  let e = 0x510e527f | 0;
  let f = 0x9b05688c | 0;
  let g = 0x1f83d9ab | 0;
  let h = 0x5be0cd19 | 0;
  for (let t = 0; t < 64; t++) {
    const t1 = (h + (((e >>> 6) | (e << 26)) ^ ((e >>> 11) | (e << 21)) ^ ((e >>> 25) | (e << 7))) + ((e & f) ^ (~e & g)) + K[t]! + W[t]!) | 0;
    const t2 = ((((a >>> 2) | (a << 30)) ^ ((a >>> 13) | (a << 19)) ^ ((a >>> 22) | (a << 10))) + ((a & b) ^ (a & c) ^ (b & c))) | 0;
    h = g;
    g = f;
    f = e;
    e = (d + t1) | 0;
    d = c;
    c = b;
    b = a;
    a = (t1 + t2) | 0;
  }
  return (a + 0x6a09e667) >>> 0;
}

/** writes "<id>:" into BUF, returns its length */
function writePrefix(id: string): number {
  if (!ID_RE.test(id)) throw new Error('bad captcha id');
  for (let i = 0; i < id.length; i++) BUF[i] = id.charCodeAt(i);
  BUF[id.length] = 58; // ':'
  return id.length + 1;
}

function hashAt(prefixLen: number, nonce: number): number {
  const s = String(nonce);
  for (let i = 0; i < s.length; i++) BUF[prefixLen + i] = s.charCodeAt(i);
  return firstWord(prefixLen + s.length);
}

/** First 32 bits (big-endian) of SHA-256("<id>:<nonce>"). */
export function powWord(id: string, nonce: number): number {
  return hashAt(writePrefix(id), nonce);
}

/** true if the 32-bit word starts with at least `bits` (0..32) zero bits */
export function meetsBits(word: number, bits: number): boolean {
  if (bits <= 0) return true;
  if (bits >= 32) return word >>> 0 === 0;
  return word >>> (32 - bits) === 0;
}

export function isValidNonce(id: string, nonce: number, bits: number): boolean {
  if (!Number.isInteger(nonce) || nonce < 0 || nonce > MAX_NONCE) return false;
  return meetsBits(powWord(id, nonce), bits);
}

/**
 * Resumable search: scans at most `budget` nonces from `state.next`, appending hits to `state.found`.
 * Returns true once `k` solutions are found (the caller yields between slices — no UI freeze).
 */
export function searchPow(id: string, bits: number, k: number, state: { found: number[]; next: number }, budget: number): boolean {
  const p = writePrefix(id);
  const end = Math.min(state.next + budget, MAX_NONCE + 1);
  let n = state.next;
  while (n < end && state.found.length < k) {
    if (meetsBits(hashAt(p, n), bits)) state.found.push(n);
    n++;
  }
  state.next = n;
  if (state.found.length < k && n > MAX_NONCE) throw new Error('captcha: nonce space exhausted');
  return state.found.length >= k;
}

/** Synchronous full solve (tests, workers). */
export function solvePow(id: string, bits: number, k: number): number[] {
  const st = { found: [] as number[], next: 0 };
  while (!searchPow(id, bits, k, st, 1 << 20));
  return st.found;
}

const B64 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';

function b64urlToText(s: string): string | null {
  if (!/^[A-Za-z0-9_-]*$/.test(s)) return null;
  const bytes: number[] = [];
  let acc = 0;
  let bits = 0;
  for (const ch of s) {
    acc = (acc << 6) | B64.indexOf(ch);
    bits += 6;
    if (bits >= 8) {
      bits -= 8;
      bytes.push((acc >> bits) & 0xff);
    }
  }
  // payload is ASCII JSON
  return bytes.every((x) => x < 0x80) ? String.fromCharCode(...bytes) : null;
}

/** Reads a challenge token `<payload>.<sig>` WITHOUT checking the signature (the client needs id/b/k/e). */
export function decodeChallenge(token: string): CaptchaPayload | null {
  const parts = token.split('.');
  if (parts.length !== 2 || !parts[0] || !parts[1]) return null;
  const text = b64urlToText(parts[0]);
  if (text === null) return null;
  let v: unknown;
  try {
    v = JSON.parse(text);
  } catch {
    return null;
  }
  const o = v as Partial<CaptchaPayload> | null;
  if (
    !o ||
    o.v !== 1 ||
    typeof o.id !== 'string' ||
    !ID_RE.test(o.id) ||
    !(CAPTCHA_PURPOSES as readonly unknown[]).includes(o.p) ||
    !Number.isInteger(o.b) ||
    o.b! < 0 ||
    o.b! > 32 ||
    !Number.isInteger(o.k) ||
    o.k! < 1 ||
    o.k! > 64 ||
    !Number.isInteger(o.e)
  ) {
    return null;
  }
  return { v: 1, id: o.id, p: o.p!, b: o.b!, k: o.k!, e: o.e! };
}
